import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useStaticQuery, graphql, Link } from 'gatsby'
import Img from 'gatsby-image'

const AUTOPLAY_DELAY = 5000
const SLIDE_GAP = 20

const Partner = styled(Link)`
  &:hover {
    opacity: 0.6;
  }

  &:hover h2 {
    text-decoration: none !important;
  }

  & h2 {
    font-size: ${({ theme }) => theme.font.size.m};
    font-weight: ${({ theme }) => theme.font.weight.l};
    text-align: center;
  }
`

const Line = styled.div`
  background: ${(props) => props.theme.color.gradient.main};
  height: 3px;
  border-radius: 1.5px;
  width: 20%;
  margin: 0 auto;
`

const PartnerImg = styled(Img)`
  left: 0;
  right: 0;
  margin: 0 auto;

  @media (min-width: 992px) {
    height: 125px;
    width: 125px;
  }

  @media (max-width: 991.98px) {
    height: 100px;
    width: 100px;
  }

  @media (max-width: 767.98px) {
    height: 90px;
    width: 90px;
  }

  @media (max-width: 575.98px) {
    height: 75px;
    width: 75px;
  }
`

const Title = styled.h2`
  font-weight: ${({ theme }) => theme.font.weight.l};
`

const SliderViewport = styled.div`
  overflow: hidden;
  width: 100%;
  margin: 0 -${SLIDE_GAP / 2}px;
  cursor: ${({ $dragging }) => ($dragging ? 'grabbing' : 'grab')};
  user-select: ${({ $dragging }) => ($dragging ? 'none' : 'auto')};

  &:active {
    cursor: grabbing;
  }
`

const SliderTrack = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: ${SLIDE_GAP}px;
  width: max-content;
  transition: ${({ $transition }) => ($transition ? 'transform 0.4s ease-out' : 'none')};
  transform: translateX(${({ $offset }) => $offset}px);
  touch-action: pan-y;
`

const Slide = styled.div`
  flex: 0 0 ${({ $width }) => $width}px;
  min-width: ${({ $width }) => $width}px;
  box-sizing: border-box;

  @media (max-width: 575.98px) {
    flex: 0 0 100px;
    min-width: 100px;
  }
`

function getSlidesPerView(initialSlides) {
  if (typeof window === 'undefined') return initialSlides
  if (window.innerWidth >= 768) return initialSlides
  if (window.innerWidth >= 450) return 4
  return 3
}

const PartnerSlider = ({ initialSlides = 5 }) => {
  const {
    partners: { nodes: partners },
  } = useStaticQuery(graphql`
    {
      partners: allWordpressWpPartner(sort: { fields: date }) {
        nodes {
          ...PreviewPartnersFrag
        }
      }
    }
  `)

  const [slidesPerView, setSlidesPerView] = useState(initialSlides)
  const [viewportWidth, setViewportWidth] = useState(0)
  const [index, setIndex] = useState(0)
  const [transitioning, setTransitioning] = useState(true)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const viewportRef = useRef(null)
  const widthStableRef = useRef(0)
  const widthLatestRef = useRef(0)
  const rafIdRef = useRef(null)
  const resizeTimeoutRef = useRef(null)
  const startXRef = useRef(0)
  const startOffsetRef = useRef(0)
  const isDraggingRef = useRef(false)
  const didDragRef = useRef(false)

  const maxIndex = partners.length

  useEffect(() => {
    const updateSlides = () => {
      const next = getSlidesPerView(initialSlides)
      setSlidesPerView((prev) => (prev === next ? prev : next))
    }
    updateSlides()
    const onResize = () => {
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current)
      resizeTimeoutRef.current = setTimeout(updateSlides, 150)
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current)
    }
  }, [initialSlides])

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return

    const flushWidth = () => {
      rafIdRef.current = null
      const w = widthLatestRef.current
      const rounded = Math.round(w / 10) * 10
      if (rounded > 0 && rounded !== widthStableRef.current) {
        widthStableRef.current = rounded
        setViewportWidth(rounded)
      }
    }

    const ro = new ResizeObserver(() => {
      widthLatestRef.current = Math.round(el.getBoundingClientRect().width)
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(flushWidth)
      }
    })
    ro.observe(el)
    return () => {
      ro.disconnect()
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current)
    }
  }, [])

  const slideWidth =
    viewportWidth > 0
      ? viewportWidth < 576
        ? 100
        : (viewportWidth - SLIDE_GAP * (slidesPerView - 1)) / slidesPerView
      : (1200 - SLIDE_GAP * (slidesPerView - 1)) / slidesPerView

  const step = slideWidth + SLIDE_GAP
  const offset = -index * step
  const displayPartners = [...partners, ...partners]

  const handlePointerDown = (e) => {
    if (e.button !== 0 && e.type === 'mousedown') return
    startXRef.current = e.clientX
    startOffsetRef.current = offset
    didDragRef.current = false
    isDraggingRef.current = true
    setIsDragging(true)
    setDragOffset(0)
    viewportRef.current?.setPointerCapture?.(e.pointerId)
  }

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return
    const dx = e.clientX - startXRef.current
    if (Math.abs(dx) > 5) didDragRef.current = true
    setDragOffset(dx)
  }

  const handlePointerUp = (e) => {
    if (!isDraggingRef.current) return
    viewportRef.current?.releasePointerCapture?.(e.pointerId)
    isDraggingRef.current = false
    setIsDragging(false)
    const dx = e.clientX - startXRef.current
    const totalOffset = startOffsetRef.current + dx
    const newIndex = Math.round(-totalOffset / step)
    const clamped = Math.max(0, Math.min(maxIndex - 1, newIndex))
    setIndex(clamped)
    setDragOffset(0)
    if (clamped === 0) setTransitioning(false)
  }

  const handlePointerCancel = () => {
    isDraggingRef.current = false
    setIsDragging(false)
    setDragOffset(0)
  }

  useEffect(() => {
    const id = setInterval(() => {
      if (isDraggingRef.current) return
      setIndex((i) => {
        const next = i >= maxIndex - 1 ? 0 : i + 1
        if (next === 0) setTransitioning(false)
        return next
      })
    }, AUTOPLAY_DELAY)
    return () => clearInterval(id)
  }, [maxIndex])

  useEffect(() => {
    if (transitioning) return
    const t = setTimeout(() => setTransitioning(true), 50)
    return () => clearTimeout(t)
  }, [transitioning])

  return (
    <div className="container">
      <Title className="mb-4 mb-lg-5">Onze partners</Title>
      <SliderViewport
        ref={viewportRef}
        $dragging={isDragging}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onClickCapture={(e) => {
          if (didDragRef.current) {
            e.preventDefault()
            e.stopPropagation()
            didDragRef.current = false
          }
        }}
      >
        <SliderTrack
          $offset={offset + dragOffset}
          $transition={transitioning && !isDragging}
        >
          {displayPartners.map((partner, i) => (
            <Slide key={`${partner.id || i}-${i}`} $width={slideWidth}>
              <Partner to={partner.path}>
                <PartnerImg
                  loading="eager"
                  fadeIn={false}
                  objectFit="contain"
                  fluid={partner.acf.preview.image.localFile.childImageSharp.fluid}
                  alt={partner.title}
                />
                <h2 className="mt-3">{partner.title}</h2>
                <Line className="mt-2" />
              </Partner>
            </Slide>
          ))}
        </SliderTrack>
      </SliderViewport>
    </div>
  )
}

export default PartnerSlider
